import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCalendarAlt,
  FaEye,
  FaCodeBranch,
  FaArrowLeft,
  FaExclamationTriangle,
  FaClock,
  FaCheckCircle,
} from "react-icons/fa";
import api from "../api/axios";
import "./DocumentManagement.css";
import { getFileIcon, getExpiryBadge } from "./docUtils";

export default function ExpiryAlerts() {
  const navigate = useNavigate();
  const [alerts7, setAlerts7] = useState([]);
  const [alerts30, setAlerts30] = useState([]);
  const [alerts90, setAlerts90] = useState([]);
  const [expired, setExpired] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [r7, r30, r90, rExp] = await Promise.all([
          api.get("/document-management/expiry/alerts", { params: { days: 7 } }),
          api.get("/document-management/expiry/alerts", { params: { days: 30 } }),
          api.get("/document-management/expiry/alerts", { params: { days: 90 } }),
          api.get("/document-management/expiry/expired"),
        ]);
        setAlerts7(r7.data || []);
        setAlerts30(r30.data || []);
        setAlerts90(r90.data || []);
        setExpired(rExp.data || []);
      } catch (e) {
        setError("Failed to load compliance expiry alerts.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const getDisplayList = () => {
    if (activeTab === "expired") return expired;
    if (activeTab === "7days") return alerts7;
    if (activeTab === "30days") return alerts30;
    if (activeTab === "90days") return alerts90;

    const all = [...expired, ...alerts90];
    const seen = new Set();
    return all.filter(d => {
      if (seen.has(d.id)) return false;
      seen.add(d.id);
      return true;
    }).sort((a, b) => {
      if (!a.expiry_date) return 1;
      if (!b.expiry_date) return -1;
      return new Date(a.expiry_date) - new Date(b.expiry_date);
    });
  };

  const list = getDisplayList();

  return (
    <div className="doc-mgmt-container">
      <div className="doc-mgmt-header">
        <div className="doc-header-left">
          <div className="doc-header-icon-box" style={{ background: "#fffbeb", color: "#d97706", borderColor: "#fde68a" }}>
            <FaCalendarAlt />
          </div>
          <div>
            <h1 className="doc-mgmt-title">Compliance Expiry Tracking</h1>
            <p className="doc-mgmt-subtitle">
              Monitor renewal deadlines for commercial licenses, employee visas, passports, and contracts
            </p>
          </div>
        </div>
        <button
          className="doc-btn-outline"
          onClick={() => navigate("/dashboard/documents/list")}
        >
          <FaArrowLeft />
          <span>All Documents</span>
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* KPI Cards */}
      <div className="expiry-summary-row">
        <div
          className="expiry-summary-card"
          style={{ borderLeft: "4px solid #e11d48", outline: activeTab === "expired" ? "2px solid #e11d48" : "none" }}
          onClick={() => setActiveTab("expired")}
        >
          <div className="doc-stat-icon-wrap rose">
            <FaExclamationTriangle />
          </div>
          <div>
            <div className="count" style={{ color: "#e11d48" }}>{expired.length}</div>
            <div className="label">Expired Documents</div>
          </div>
        </div>

        <div
          className="expiry-summary-card"
          style={{ borderLeft: "4px solid #ea580c", outline: activeTab === "7days" ? "2px solid #ea580c" : "none" }}
          onClick={() => setActiveTab("7days")}
        >
          <div className="doc-stat-icon-wrap amber" style={{ background: "#fff7ed", color: "#ea580c" }}>
            <FaClock />
          </div>
          <div>
            <div className="count" style={{ color: "#ea580c" }}>{alerts7.length}</div>
            <div className="label">Expiring in 7 Days</div>
          </div>
        </div>

        <div
          className="expiry-summary-card"
          style={{ borderLeft: "4px solid #d97706", outline: activeTab === "30days" ? "2px solid #d97706" : "none" }}
          onClick={() => setActiveTab("30days")}
        >
          <div className="doc-stat-icon-wrap amber">
            <FaClock />
          </div>
          <div>
            <div className="count" style={{ color: "#d97706" }}>{alerts30.length}</div>
            <div className="label">Expiring in 30 Days</div>
          </div>
        </div>

        <div
          className="expiry-summary-card"
          style={{ borderLeft: "4px solid #0284c7", outline: activeTab === "90days" ? "2px solid #0284c7" : "none" }}
          onClick={() => setActiveTab("90days")}
        >
          <div className="doc-stat-icon-wrap primary">
            <FaClock />
          </div>
          <div>
            <div className="count" style={{ color: "#0284c7" }}>{alerts90.length}</div>
            <div className="label">Expiring in 90 Days</div>
          </div>
        </div>
      </div>

      {/* Tab Filter Strip */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[
          { key: "all", label: "All Upcoming Deadlines" },
          { key: "expired", label: `Expired (${expired.length})` },
          { key: "7days", label: `Within 7 Days (${alerts7.length})` },
          { key: "30days", label: `Within 30 Days (${alerts30.length})` },
          { key: "90days", label: `Within 90 Days (${alerts90.length})` },
        ].map(t => (
          <button
            key={t.key}
            className={`doc-btn-outline ${activeTab === t.key ? "doc-btn-primary" : ""}`}
            style={activeTab === t.key ? { background: "#0284c7", color: "#ffffff", borderColor: "#0284c7" } : {}}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="doc-table-wrapper">
        {loading ? (
          <div style={{ textAlign: "center", padding: 56 }}>
            <div className="spinner-border text-primary" />
            <div style={{ marginTop: 12, color: "#64748b", fontSize: "13px" }}>Analyzing compliance records...</div>
          </div>
        ) : list.length === 0 ? (
          <div className="doc-empty">
            <div className="doc-empty-icon" style={{ background: "#ecfdf5", color: "#059669" }}>
              <FaCheckCircle />
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#0f172a" }}>No pending expirations</div>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
              All monitored records in this window are valid and up to date.
            </div>
          </div>
        ) : (
          <table className="doc-table">
            <thead>
              <tr>
                <th>Document Name</th>
                <th>Category</th>
                <th>Associated Party</th>
                <th>Expiry Date</th>
                <th>Timeline Remaining</th>
                <th>Compliance Status</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map(d => (
                <tr key={d.id} onClick={() => navigate(`/dashboard/documents/${d.id}`)}>
                  <td>
                    <div className="doc-title-cell">
                      {getFileIcon(null, d.title)}
                      <div className="doc-title-text">{d.title}</div>
                    </div>
                  </td>
                  <td>
                    <span className="cat-badge">{d.category}</span>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {d.entity_name ? (
                      <span>
                        <span style={{ color: "#64748b", textTransform: "capitalize" }}>{d.entity_type}: </span>
                        <strong>{d.entity_name}</strong>
                      </span>
                    ) : (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    )}
                  </td>
                  <td style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>
                    {d.expiry_date ? new Date(d.expiry_date).toLocaleDateString() : "—"}
                  </td>
                  <td>
                    {d.days_remaining !== null && d.days_remaining !== undefined ? (
                      <span style={{
                        fontWeight: 700,
                        fontSize: "12px",
                        color: d.days_remaining < 0 ? "#dc2626" : d.days_remaining <= 7 ? "#ea580c" : d.days_remaining <= 30 ? "#d97706" : "#0284c7",
                      }}>
                        {d.days_remaining < 0
                          ? `${Math.abs(d.days_remaining)} days overdue`
                          : d.days_remaining === 0
                            ? "Expires today"
                            : `${d.days_remaining} days remaining`}
                      </span>
                    ) : "—"}
                  </td>
                  <td>{getExpiryBadge(d.expiry_status)}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className="doc-actions-wrap" style={{ justifyContent: "flex-end" }}>
                      <button
                        className="doc-action-btn"
                        title="View Document Record"
                        onClick={() => navigate(`/dashboard/documents/${d.id}`)}
                      >
                        <FaEye />
                      </button>
                      <button
                        className="doc-action-btn"
                        title="Upload Renewal Revision"
                        onClick={() => navigate(`/dashboard/documents/${d.id}?tab=versions`)}
                      >
                        <FaCodeBranch />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
